import React from "react";
import { Loader2 } from "lucide-react";

/**
 * Primary button component with loading state
 *
 * @param {Object} props - Component props
 * @param {string} props.children - Button text
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.isLoading - Whether the button is in loading state
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.loadingText - Text to show when loading (defaults to "Loading...")
 * @param {string} props.type - Button type (defaults to "button")
 * @param {string} props.className - Additional class names
 * @param {string} props.width - Width of the button (full, auto)
 * @param {string} props.size - Size of the button (sm, md, lg)
 * @returns {JSX.Element}
 */
const PrimaryButton = ({
  children,
  onClick,
  isLoading = false,
  disabled = false,
  loadingText = "Loading...",
  type = "button",
  className = "",
  width = "full",
  size = "md",
  ...props
}) => {
  // Size classes
  const sizeClasses = {
    sm: "px-3 py-2 text-xs",
    md: "px-4 py-3 text-sm",
    lg: "px-5 py-4 text-base",
  };

  // Width classes
  const widthClasses = {
    full: "w-full",
    auto: "w-auto",
  };

  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={`
        ${widthClasses[width] || widthClasses.full}
        ${sizeClasses[size] || sizeClasses.md}
        font-medium rounded-lg text-white
        ${
          isDisabled
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
        }
        transition-colors duration-200
        flex items-center justify-center
        ${className}
      `}
      {...props}
    >
      {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
      {isLoading ? loadingText : children}
    </button>
  );
};

export default PrimaryButton;
